import React from "react";
import { useMsal } from "@azure/msal-react";
import { Navigate, useLocation } from "react-router-dom";
import { useUserSession } from "../../session/useUserSession";
import { debugAuthSnapshot } from "../../utils/debugAuthSnapshot";

const ROLE_HOME_PAGES = {
  "Customer": "/microgrid-page-design",
  "Employee": "/customer-projects",
  "Branch Admin": "/customer-projects",
  "Organization Admin": "/branchhomepage",
};

const ProtectedRoute = ({ children, allowedRoles }) => {
  // Keep context read so SessionPopup can still function, even if we don't gate on these flags.
  useUserSession();
  const { inProgress } = useMsal();
  const token = sessionStorage.getItem("accessToken");
  const location = useLocation();

  // Define excluded routes where session checks are skipped
  const excludedRoutes = [
    "/login",
    "/",
    "/#/",
    "/forgot-password",
    "/register",
    "/reset-password",
  ];

  // Skip session checks for excluded routes
  if (excludedRoutes.includes(location.pathname)) {
    return children;
  }

  // During MSAL popup/redirect/silent auth, the token may not be in storage yet.
  // Avoid redirect flicker by waiting until MSAL finishes.
  const isMsalRedirect =
    window.location.hash.includes("state=") &&
    (window.location.hash.includes("code=") ||
      window.location.hash.includes("error="));
  if (isMsalRedirect || inProgress !== "none") {
    debugAuthSnapshot("ProtectedRoute:wait", {
      routePathname: location?.pathname,
      inProgress,
      isMsalRedirect,
      tokenPresent: !!token,
    });
    return (
      <div className="content" style={{ padding: 24 }}>
        <div style={{ textAlign: "center", marginTop: 48 }}>
          <p style={{ fontSize: 16, marginBottom: 16 }}>
            Completing sign-in, please wait...
          </p>
          <i
            className="fas fa-spinner fa-spin"
            style={{ fontSize: 32, color: "#4CAF50" }}
          ></i>
        </div>
      </div>
    );
  }

  // Block access only if token is missing (user is not logged in)
  if (!token) {
    debugAuthSnapshot("ProtectedRoute:redirect_to_login", {
      routePathname: location?.pathname,
      inProgress,
      isMsalRedirect,
      tokenPresent: false,
    });
    return <Navigate to="/" replace />;
  }

  if (allowedRoles) {
    const jobTitle = sessionStorage.getItem("jobTitle");
    if (!allowedRoles.includes(jobTitle)) {
      const homePage = ROLE_HOME_PAGES[jobTitle] || "/";
      debugAuthSnapshot("ProtectedRoute:role_denied", {
        routePathname: location?.pathname,
        jobTitle,
        allowedRoles,
        redirectTo: homePage,
      });
      return <Navigate to={homePage} replace />;
    }
  }

  debugAuthSnapshot("ProtectedRoute:allow", {
    routePathname: location?.pathname,
    inProgress,
    isMsalRedirect,
    tokenPresent: true,
  });
  return children;
};

export default ProtectedRoute;
